// src/lib/resources/relevance/calculator-scoring.ts

/**
 * Pure relevance-scoring functions for the calculator detail page's
 * "Related X" cluster. Used by:
 *
 *   - `~/lib/resources/api/related-resources.ts`
 *     (server-side, precomputed and passed via prop on first paint)
 *   - `~/components/resources/calculators/CalculatorLayout.tsx`
 *     (client-side fallback path for legacy callers that don't
 *     pre-compute, e.g. `DynamicResourceComponent`)
 *
 * Lives in a `'use server'`/`'use client'`-free module so both
 * surfaces can import directly — no Server-Reference wrapping, no
 * client-bundle pollution. (The previous "duplicate to keep the
 * server module dependency-clean" rationale was incorrect: importing
 * named exports of pure functions doesn't drag React-Motion / Next-
 * Image bundles in either direction.)
 *
 * No state, no effects, no fetches — these are dispatch-grade pure
 * functions over already-loaded resource records.
 */

import type { ResourceTypeMap } from '~/lib/resources/types/shared/base'
import { getMetricTermVariableNames } from '~/lib/resources/types/shared/property-accessors'
import { wordContainmentRatio } from '~/lib/utils/similarity'
import { scoreSharedTopicHubAffinity, scoreGlossaryTopicHubAffinity } from '~/lib/resources/relevance/topic-hub-affinity'

type ScoringTool = Extract<ResourceTypeMap['tool'], { toolType: 'calculator' | 'analyzer' }>
type GlossaryTerm = ResourceTypeMap['glossary-term']

/**
 * Score how related a sibling tool is to the current calculator or analyzer.
 * Higher scores mean "more relevant"; 0 means "not enough signal."
 *
 * Weighting rationale, derived from the legacy in-component scorer:
 *   - Category match (resource category or toolCategory):     +5 each
 *   - Description / keyword superset match (either direction): +10
 *   - Per-matching-keyword bonus (case-insensitive substring): +2
 *
 * Analyzer pages reuse this scorer intentionally — analyzers and calculators
 * share the same tool metadata shape and cross-linking goals on detail pages.
 */
export function scoreToolForCalculator(
  tool: ResourceTypeMap['tool'],
  currentTool: ScoringTool,
): number {
  let score = 0

  if (tool.category === currentTool.category) score += 5
  if (tool.toolCategory === currentTool.toolCategory) score += 5

  const toolContent = [
    tool.title,
    tool.description,
    tool.metadata?.keywords?.join(' ') ?? '',
    tool.category ?? '',
    tool.toolCategory ?? '',
  ]
    .join(' ')
    .toLowerCase()

  const currentContent = [
    currentTool.title,
    currentTool.description,
    currentTool.metadata?.keywords?.join(' ') ?? '',
    currentTool.category ?? '',
    currentTool.toolCategory ?? '',
  ]
    .join(' ')
    .toLowerCase()

  if (toolContent.includes(currentContent) || currentContent.includes(toolContent)) {
    score += 10
  }

  const toolKeywords = tool.metadata?.keywords ?? []
  const currentKeywords = currentTool.metadata?.keywords ?? []
  const keywordMatches = toolKeywords.filter((k) =>
    currentKeywords.some(
      (ck) =>
        k.toLowerCase().includes(ck.toLowerCase()) ||
        ck.toLowerCase().includes(k.toLowerCase()),
    ),
  ).length
  score += keywordMatches * 2

  score += scoreSharedTopicHubAffinity(currentTool.slug, tool.slug)

  return score
}

/**
 * Score how related a glossary term is to the current calculator.
 *
 * Title / acronym appearing in the calculator copy is the strongest
 * signal (+50 for title in the calculator's own title, +40 for
 * appearing anywhere in calculator content). Word-containment ratio
 * + metric-variable matches surface less-obvious thematic
 * relationships. Related-term echoes (this term's `relatedTerms`
 * appearing in the calculator copy) add a smaller bonus.
 *
 * The metric-only branches (`term.category === 'metrics'`) are
 * intentional: those bonuses don't apply to creative/general/chart
 * terms because the corresponding signal (per-metric variable
 * matching, metric+analytics-tool affinity) isn't defined there.
 */
export function scoreGlossaryForCalculator(
  term: GlossaryTerm,
  calculator: ScoringTool,
): number {
  let score = 0

  const termTitle = term.title.toLowerCase()
  const termAcronym = term.acronym?.toLowerCase() ?? ''
  const termShortDesc = term.shortDescription?.toLowerCase() ?? ''
  const termDefinition = term.definition?.toLowerCase() ?? ''
  const termKeywords = term.metadata?.keywords ?? []

  const calcContent = [
    calculator.title,
    calculator.description,
    calculator.metadata?.keywords?.join(' ') ?? '',
    calculator.category ?? '',
    calculator.toolCategory ?? '',
    calculator.slug.split('-').join(' '),
  ]
    .join(' ')
    .toLowerCase()

  if (
    calculator.title.toLowerCase().includes(termTitle) ||
    (termAcronym && calculator.title.toLowerCase().includes(termAcronym))
  ) {
    score += 50
  }

  if (calcContent.includes(termTitle) || (termAcronym && calcContent.includes(termAcronym))) {
    score += 40
  }

  const termContent = [
    termTitle,
    termAcronym,
    termShortDesc,
    termDefinition,
    termKeywords.join(' '),
  ]
    .join(' ')
    .toLowerCase()

  const containmentRatio = wordContainmentRatio(calcContent, termContent, {
    minWordLength: 3,
    ignoreCommonWords: true,
  })
  score += Math.round(containmentRatio * 20)

  if (term.category === 'metrics') {
    const metricVariables = getMetricTermVariableNames(term)
    if (metricVariables.length > 0) {
      const variableMatches = metricVariables.filter((variable) =>
        calcContent.includes(variable.toLowerCase()),
      ).length
      score += variableMatches * 4
    }
  }

  if (
    term.category === 'metrics' &&
    (calculator.toolCategory === 'analytics' || calculator.category === 'metrics')
  ) {
    score += 8
  }

  if (term.relatedTerms?.length) {
    const relatedMatches = term.relatedTerms.filter((rt) =>
      calcContent.includes(rt.title.toLowerCase()),
    ).length
    score += relatedMatches * 3
  }

  score += scoreGlossaryTopicHubAffinity(term.category, term.slug, calculator.slug)

  return score
}
